import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import api from '../../services/api.service';
import './todo.styles.scss';

/**
 * TodoComponent - A reusable todo component for project tasks
 * 
 * @param {Object} props 
 * @param {string} props.projectId - The ID of the project these todos belong to
 * @param {Object} props.currentUser - The currently logged in user (from Redux)
 */
const TodoComponent = ({ projectId, currentUser }) => {

  const [todos, setTodos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newTodoDescription, setNewTodoDescription] = useState('');
  const [isAddingTodo, setIsAddingTodo] = useState(false);
  const [editingTodoId, setEditingTodoId] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const textareaRef = useRef(null);

  // Fetch todos when component mounts or projectId changes
  useEffect(() => {
    if (projectId) {
      fetchTodos();
    }
  }, [projectId]);

  // Focus textarea when adding or editing a todo
  useEffect(() => {
    if ((isAddingTodo || editingTodoId) && textareaRef.current) {
      textareaRef.current.focus();
      
      // Auto-resize the textarea when it receives focus
      autoResizeTextarea(textareaRef.current);
    }
  }, [isAddingTodo, editingTodoId]);

  // Auto-resize textarea function
  const autoResizeTextarea = (textarea) => {
    if (!textarea) return;
    
    // Reset height to get the correct scrollHeight
    textarea.style.height = 'auto';
    
    // Set new height based on scrollHeight (content height)
    textarea.style.height = textarea.scrollHeight + 'px';
  };

  // Handle input change - resize and update state
  const handleTextareaChange = (e) => {
    setNewTodoDescription(e.target.value);
    autoResizeTextarea(e.target);
  };

  // Handle keydown in textarea
  const handleKeyDown = (e) => {
    // If Enter key is pressed (without Shift key for newline)
    if (e.key === 'Enter' && !e.shiftKey) {
      // Prevent submission if textarea is empty
      if (!newTodoDescription.trim()) return;
      
      e.preventDefault(); // Prevent the default action (newline)
      
      // Save or update depending on current mode
      if (editingTodoId) {
        handleUpdateTodo();
      } else {
        handleAddTodo();
      }
    }
  };

  // Fetch todos from the API
  const fetchTodos = async () => {
    setIsLoading(true);
    try {
      const fetchedTodos = await api.todo.get(`?projectId=${projectId}`);
      setTodos(fetchedTodos || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching todos:', err);
      setError('Could not load todos. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Add a new todo
  const handleAddTodo = async () => {
    if (!newTodoDescription.trim()) return;

    try {
      setError(null); // Clear any previous errors
      
      // Create todo data object
      const todoData = {
        description: newTodoDescription,
        projectId,
        status: 'open'
      };
      
      const newTodo = await api.todo.post(todoData);
      
      // If we got a successful response, update the UI
      setTodos(prevTodos => [...prevTodos, newTodo]);
      setNewTodoDescription('');
      setIsAddingTodo(false);
    } catch (err) {
      console.error('Error adding todo:', err);
      
      // Provide more specific error message if available
      if (err.message && err.message.includes('Missing required fields')) {
        setError('Missing required information. Please ensure all fields are filled correctly.');
      } else if (err.name === 'TypeError' && err.message.includes('Failed to fetch')) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError('Failed to add todo. Please try again.');
      }
    }
  };

  // Start editing a todo
  const handleEditTodo = (todoId) => {
    const todoToEdit = todos.find(todo => todo.id === todoId);
    if (todoToEdit) {
      setEditingTodoId(todoId);
      setNewTodoDescription(todoToEdit.description);
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setIsAddingTodo(false);
    setEditingTodoId(null);
    setNewTodoDescription('');
  };

  // Update an existing todo
  const handleUpdateTodo = async () => {
    if (!newTodoDescription.trim()) return;
    
    try {
      const todoToUpdate = todos.find(todo => todo.id === editingTodoId);
      const updatedTodo = await api.todo.put('', {
        ...todoToUpdate,
        description: newTodoDescription
      });
      
      setTodos(prevTodos => 
        prevTodos.map(todo => todo.id === editingTodoId ? updatedTodo : todo)
      );
      
      setEditingTodoId(null);
      setNewTodoDescription('');
    } catch (err) {
      console.error('Error updating todo:', err);
      setError('Failed to update todo. Please try again.');
    }
  };

  // Change the status of a todo
  const handleStatusChange = async (todoId, newStatus) => {
    try {
      const todoToUpdate = todos.find(todo => todo.id === todoId);
      const updatedTodo = await api.todo.put('', {
        ...todoToUpdate,
        status: newStatus
      });
      
      setTodos(prevTodos => 
        prevTodos.map(todo => todo.id === todoId ? updatedTodo : todo)
      );
    } catch (err) {
      console.error('Error updating todo status:', err);
      setError('Failed to update todo status. Please try again.');
    }
  };

  // Delete a todo
  const handleDeleteTodo = async (todoId) => {
    try {
      await api.todo.delete(`${todoId}`);
      setTodos(prevTodos => prevTodos.filter(todo => todo.id !== todoId));
    } catch (err) {
      console.error('Error deleting todo:', err);
      setError('Failed to delete todo. Please try again.');
    }
  };

  // Check if current user is the creator of a todo item
  const isCreator = (todo) => {
    if (!currentUser || !todo || !todo.createdByUserId) return false;
    // Convert both IDs to strings to ensure consistent comparison
    return String(todo.createdByUserId) === String(currentUser.id);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('no-NO');
  };

  // Get filtered todos based on status filter
  const getFilteredTodos = () => {
    if (filterStatus === 'all') {
      return todos;
    }
    return todos.filter(todo => todo.status === filterStatus);
  };

  // Get count of todos by status
  const getStatusCounts = () => {
    const counts = {
      open: 0,
      inOrder: 0,
      readyForAssembly: 0,
      done: 0
    };
    
    todos.forEach(todo => {
      if (counts[todo.status] !== undefined) {
        counts[todo.status]++;
      }
    });
    
    return counts;
  };

  // Status labels for better display
  const statusLabels = {
    open: 'Åpen',
    inOrder: 'Bestillt',
    readyForAssembly: 'Klart til montering',
    done: 'Utført'
  };

  // Status colors for visual indication
  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'status-open';
      case 'inOrder': return 'status-ordered';
      case 'readyForAssembly': return 'status-ready';
      case 'done': return 'status-done';
      default: return '';
    }
  };
  
  // Render loading state
  if (isLoading && !todos.length) {
    return <div className="todo-loading">Laster sjekkliste...</div>;
  }

  const filteredTodos = getFilteredTodos();
  const statusCounts = getStatusCounts();

  return (
    <div className="todo-container">
      {error && <div className="todo-error">{error}</div>}
      
      <div className="todo-header">
        <div className="todo-filters">
          <button 
            type="button"
            className={`todo-filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
            onClick={() => setFilterStatus('all')}
          >
            Alle ({todos.length})
          </button>
          <button 
            type="button"
            className={`todo-filter-btn ${filterStatus === 'open' ? 'active' : ''} status-open`}
            onClick={() => setFilterStatus('open')}
          >
            Åpne ({statusCounts.open})
          </button>
          <button 
            type="button"
            className={`todo-filter-btn ${filterStatus === 'inOrder' ? 'active' : ''} status-ordered`}
            onClick={() => setFilterStatus('inOrder')}
          >
            Bestilt ({statusCounts.inOrder})
          </button>
          <button 
            type="button"
            className={`todo-filter-btn ${filterStatus === 'readyForAssembly' ? 'active' : ''} status-ready`}
            onClick={() => setFilterStatus('readyForAssembly')}
          >
            Klart til montering ({statusCounts.readyForAssembly})
          </button>
          <button 
            type="button"
            className={`todo-filter-btn ${filterStatus === 'done' ? 'active' : ''} status-done`}
            onClick={() => setFilterStatus('done')}
          >
            Utført ({statusCounts.done})
          </button>
        </div>
        
        <button
          type="button"
          className="btn small light add todo-add-btn"
          onClick={() => setIsAddingTodo(true)}
        >
          Legg til sjekkliste punkt
        </button>
      </div>
      
      {isAddingTodo && !editingTodoId ? (
        <div className="todo-form-container">
          <div className="todo-form">
            <textarea
              ref={editingTodoId ? null : textareaRef}
              value={newTodoDescription}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              placeholder="Beskriv gjøremålet..."
              rows="1"
              className="todo-textarea"
            />
            <div className="todo-form-actions">
              <button
                type="button"
                className="btn small light"
                onClick={handleAddTodo}
                disabled={!newTodoDescription.trim()}
              >
                Lagre
              </button>
              <button
                type="button"
                className="btn small"
                onClick={handleCancelEdit}
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      ) : null}
      
      {filteredTodos.length > 0 ? (
        <div className="todo-list">
          {filteredTodos.map(todo => (
            <div className={`todo-item ${getStatusColor(todo.status)}`} key={todo.id}>
              {editingTodoId === todo.id ? (
                <div className="todo-edit-form">
                  <textarea
                    ref={textareaRef}
                    value={newTodoDescription}
                    onChange={handleTextareaChange}
                    onKeyDown={handleKeyDown}
                    placeholder="Beskriv gjøremålet..."
                    rows="1"
                    className="todo-textarea"
                  />
                  <div className="todo-form-actions">
                    <button
                      type="button"
                      className="btn small light"
                      onClick={handleUpdateTodo}
                      disabled={!newTodoDescription.trim()}
                    >
                      Oppdater
                    </button>
                    <button
                      type="button"
                      className="btn small"
                      onClick={handleCancelEdit}
                    >
                      Avbryt
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="todo-main">
                    <div 
                      className={`todo-content ${isCreator(todo) ? 'editable' : ''}`} 
                      onClick={() => isCreator(todo) ? handleEditTodo(todo.id) : null}
                    >
                      {todo.description}
                    </div>
                    <div className="todo-actions">
                      <select
                        value={todo.status}
                        onChange={(e) => handleStatusChange(todo.id, e.target.value)}
                        className={`todo-status-select ${getStatusColor(todo.status)}`}
                        onClick={e => e.stopPropagation()}
                      >
                        <option value="open">Åpen</option>
                        <option value="inOrder">Bestillt</option>
                        <option value="readyForAssembly">Klart til montering</option>
                        <option value="done">Utført</option>
                      </select>
                      <button 
                        type="button" 
                        className={`todo-edit-btn ${!isCreator(todo) ? 'disabled' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isCreator(todo)) {
                            handleEditTodo(todo.id);
                          }
                        }}
                        title={isCreator(todo) ? "Rediger" : "Kun oppretter kan redigere innhold"}
                        disabled={!isCreator(todo)}
                      >
                        <span className="icon-edit"></span>
                      </button>
                      <button 
                        type="button" 
                        className={`todo-delete-btn ${!isCreator(todo) ? 'disabled' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isCreator(todo)) {
                            handleDeleteTodo(todo.id);
                          }
                        }}
                        title={isCreator(todo) ? "Slett" : "Kun oppretter kan slette oppgaven"}
                        disabled={!isCreator(todo)}
                      >
                        <span className="icon-delete"></span>
                      </button>
                    </div>
                  </div>
                  <div className="todo-meta">
                    <span>Opprettet av <strong>{todo.createdByUserFullName ?? 'Ghost in the machine'}</strong> den {formatDate(todo.createdDate)}</span>
                    {todo.updatedStatusDate && (
                      <span className="meta-separator"> | Status endret av <strong>{todo.updatedStatusByUserFullName ?? 'Ghost in the machine'}</strong> den {formatDate(todo.updatedStatusDate)}</span>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="todo-empty">
          {todos.length === 0 
            ? "Ingen sjekkliste lagt til" 
            : "Ingen sjekkliste punkt matcher det valgte filteret"}
        </div>
      )}
    </div>
  );
};

// Map Redux state to component props
const mapStateToProps = (state) => ({
  currentUser: state.user.currentUser,
});

export default connect(mapStateToProps)(TodoComponent);